import EthereumConfig from "./ethereum.js";
import NearConfig from "./near.js";
import SolanaConfig from "./solana.js";
import ERC20Config from "./erc20.js";
import axios from "axios";
import utils from "../../common/utils.js";
import AptosConfig from "./aptos.js";
import { EthereumEthersV5 } from "../providers/ethereum/ethersv5.js";
import { EthereumEthersV6 } from "../providers/ethereum/ethersv6.js";
import ArweaveConfig from "./arweave.js";
import { augmentTokenPrivy } from "../shims/privy.js";
import { augmentViemV2 } from "../shims/viemv2.js";
import { Network } from "@aptos-labs/ts-sdk";
export default function getTokenConfig({ irys, token, wallet, providerUrl, contractAddress, providerName, tokenOpts: opts, }) {
    switch (token) {
        case "ethereum":
            return resolveProvider({
                family: "ethereum",
                providerName,
                config: {
                    irys: irys,
                    name: "ethereum",
                    ticker: "ETH",
                    providerUrl: providerUrl ?? "https://cloudflare-eth.com/",
                    wallet: wallet,
                    opts,
                },
            });
        case "matic":
            return resolveProvider({
                family: "ethereum",
                providerName,
                config: {
                    irys: irys,
                    name: "matic",
                    ticker: "MATIC",
                    providerUrl: providerUrl ?? "https://polygon-rpc.com",
                    wallet: wallet,
                    minConfirm: 1,
                    opts,
                },
            });
        case "arbitrum":
            return resolveProvider({
                family: "ethereum",
                providerName,
                config: {
                    irys: irys,
                    name: "arbitrum",
                    ticker: "ETH",
                    providerUrl: providerUrl ?? "https://arb1.arbitrum.io/rpc",
                    wallet: wallet,
                    opts,
                },
            });
        case "bnb":
            return resolveProvider({
                family: "ethereum",
                providerName,
                config: {
                    irys: irys,
                    name: "bnb",
                    ticker: "BNB",
                    providerUrl: providerUrl ?? "https://bsc-dataseed.binance.org",
                    wallet: wallet,
                    opts,
                },
            });
        case "avalanche":
            return resolveProvider({
                family: "ethereum",
                providerName,
                config: {
                    irys: irys,
                    name: "avalanche",
                    ticker: "AVAX",
                    providerUrl: providerUrl ?? "https://api.avax.network/ext/bc/C/rpc",
                    wallet: wallet,
                    opts,
                },
            });
        case "boba-eth":
            return resolveProvider({
                family: "ethereum",
                providerName,
                config: {
                    irys: irys,
                    name: "boba-eth",
                    ticker: "ETH",
                    providerUrl: providerUrl ?? "https://mainnet.boba.network/",
                    minConfirm: 1,
                    wallet: wallet,
                    opts,
                },
            });
        case "boba": {
            const k = new ERC20Config({
                irys: irys,
                name: "boba",
                ticker: "BOBA",
                providerUrl: providerUrl ?? "https://mainnet.boba.network/",
                contractAddress: contractAddress ?? "0xa18bF3994C0Cc6E3b63ac420308E5383f53120D7",
                minConfirm: 1,
                wallet: wallet,
                opts,
            });
            // for L1 mainnet: "https://main-light.eth.linkpool.io/" and "0x42bbfa2e77757c645eeaad1655e0911a7553efbc"
            k.price = async () => {
                const res = await axios.post("https://api.livecoinwatch.com/coins/single", JSON.stringify({ currency: "USD", code: `${k.ticker}` }), {
                    headers: { "x-api-key": "75a7a824-6577-45e6-ad86-511d590c7cc8", "content-type": "application/json" },
                });
                await utils.checkAndThrow(res, "Getting price data");
                if (!res?.data?.rate) {
                    throw new Error(`unable to get price for ${k.name}`);
                }
                return +res.data.rate;
            };
            return k;
        }
        case "solana":
            return new SolanaConfig({
                irys: irys,
                name: "solana",
                ticker: "SOL",
                providerUrl: providerUrl ?? "https://api.mainnet-beta.solana.com/",
                wallet: wallet,
                opts,
            });
        // case "algorand":
        //     return new AlgorandConfig({ name: "algorand", ticker: "ALGO", providerUrl: providerUrl ?? "https://api.mainnet-beta.solana.com/", wallet: wallet })
        case "near":
            return new NearConfig({
                irys: irys,
                name: "near",
                ticker: "NEAR",
                providerUrl: providerUrl ?? "https://rpc.mainnet.near.org",
                wallet: wallet,
                opts,
            });
        case "aptos":
            return new AptosConfig({
                irys: irys,
                name: "aptos",
                ticker: "APTOS",
                providerUrl: providerUrl ?? Network.MAINNET,
                wallet: wallet,
                opts,
            });
        case "arweave":
            return new ArweaveConfig({
                irys: irys,
                name: "arweave",
                ticker: "AR",
                providerUrl: providerUrl ?? "https://arweave.net",
                wallet: wallet,
                opts,
            });
        case "base-eth":
            return new EthereumConfig({
                irys: irys,
                name: "base-eth",
                ticker: "ETH",
                providerUrl: providerUrl ?? "https://mainnet.base.org/",
                minConfirm: 2,
                wallet: wallet,
                opts,
            });
        case "usdc-eth":
            return new ERC20Config({
                irys: irys,
                name: "usdc-eth",
                ticker: "USDC",
                providerUrl: providerUrl ?? "https://cloudflare-eth.com/",
                contractAddress: contractAddress ?? "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48",
                wallet,
                opts,
            });
        case "usdc-polygon":
            return new ERC20Config({
                irys,
                name: "usdc-polygon",
                ticker: "USDC",
                providerUrl: providerUrl ?? "https://polygon-rpc.com",
                contractAddress: contractAddress ?? "0x3c499c542cef5e3811e1192ce70d8cc03d5c3359",
                opts,
            });
        case "bera":
            return resolveProvider({
                family: "ethereum",
                providerName,
                config: {
                    irys: irys,
                    name: "bera",
                    ticker: "BERA",
                    // TODO: make sure this is set to mainnet
                    providerUrl: providerUrl ?? "https://artio.rpc.berachain.com/",
                    wallet: wallet,
                    opts,
                },
            });
        default:
            throw new Error(`Unknown/Unsupported token ${token}`);
    }
}
function resolveProvider({ family, providerName, config, }) {
    let cfg;
    switch (family) {
        case "ethereum":
            switch (providerName) {
                case "ethersv5":
                    return new EthereumEthersV5(config);
                case "ethersv6":
                    return new EthereumEthersV6(config);
                case "privy-embedded":
                    cfg = new EthereumEthersV5(config);
                    augmentTokenPrivy(cfg, config.opts);
                    return cfg;
                case "viemv2":
                    cfg = new EthereumConfig(config);
                    augmentViemV2(cfg, config.opts);
                    return cfg;
                default:
                    return new EthereumConfig(config);
            }
        default:
            throw new Error(`Unknown token family ${family}`);
    }
}
//# sourceMappingURL=index.js.map